/**
 * Frontend logging utility
 *
 * Provides structured logging for client-side operations.
 * In production, these logs could be sent to an error tracking service.
 */

interface LogData {
  [key: string]: any;
}

class Logger {
  private prefix = '[Vocabulizer]';
  private isDevelopment = import.meta.env.DEV;

  /**
   * Log a debug message (only in development)
   */
  debug(message: string, data?: LogData): void {
    if (this.isDevelopment) {
      console.debug(`${this.prefix} ${message}`, data || '');
    }
  }

  /**
   * Log an informational message
   */
  info(message: string, data?: LogData): void {
    console.info(`${this.prefix} ${message}`, data || '');
  }

  /**
   * Log a warning
   */
  warn(message: string, data?: LogData): void {
    console.warn(`${this.prefix} ${message}`, data || '');
  }

  /**
   * Log an error
   */
  error(message: string, error?: Error | LogData, data?: LogData): void {
    if (error instanceof Error) {
      console.error(`${this.prefix} ${message}`, {
        error: error.message,
        stack: error.stack,
        ...data
      });
    } else {
      console.error(`${this.prefix} ${message}`, error || '');
    }
  }

  /**
   * Time the duration of an operation
   */
  async timeAsync<T>(
    operationName: string,
    fn: () => Promise<T>,
    data?: LogData
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = Math.round(performance.now() - start);
      this.debug(`${operationName} completed`, { ...data, durationMs: duration });
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      this.error(`${operationName} failed`, error as Error, { ...data, durationMs: duration });
      throw error;
    }
  }
}

export const logger = new Logger();
export default logger;
